'use client';
import React, { useState } from 'react';
import { useLanguage } from '@/lib/LanguageContext';
import { useApp } from '@/lib/AppContext';
import { t } from '@/lib/i18n';
import AdminOverview from './AdminOverview';
import AdminLessons from './AdminLessons';
import AdminUsers from './AdminUsers';
import AdminStatsChart from './AdminStatsChart';
import { LayoutDashboard, BookOpen, Users, BarChart2, FileText, ShieldCheck, Download } from 'lucide-react';
import { adminStats } from '@/lib/mockData';
import { toast } from 'sonner';

type AdminTab = 'overview' | 'lessons' | 'users' | 'stats' | 'reports';

export default function AdminContent() {
  const { language } = useLanguage();
  const { user } = useApp();
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');

  const tabs: { key: AdminTab; icon: React.ReactNode; label: string }[] = [
    { key: 'overview', icon: <LayoutDashboard size={16} />, label: t(language, 'admin_overview') },
    { key: 'lessons', icon: <BookOpen size={16} />, label: t(language, 'admin_lessons') },
    { key: 'users', icon: <Users size={16} />, label: t(language, 'admin_users') },
    { key: 'stats', icon: <BarChart2 size={16} />, label: t(language, 'admin_stats') },
    { key: 'reports', icon: <FileText size={16} />, label: t(language, 'admin_reports') },
  ];

  const handleExport = (type: string) => {
    toast.success(
      language === 'fr'
        ? `Rapport "${type}" exporté avec succès`
        : `Отчет "${type}" успешно экспортирован`
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <ShieldCheck size={20} className="text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-700 text-foreground">{t(language, 'admin_title')}</h1>
          <p className="text-sm text-muted-foreground">
            {language === 'fr'
              ? `Connecté en tant que ${user?.name} — Gérez la plateforme Vika Comptable`
              : `Вошли как ${user?.name} — Управление платформой Vika Comptable`}
          </p>
        </div>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 bg-card border border-border rounded-xl p-1 w-fit overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={`admin-tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-600 transition-all duration-150 whitespace-nowrap ${
              activeTab === tab.key
                ? 'bg-primary text-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && <AdminOverview language={language} />}
      {activeTab === 'lessons' && <AdminLessons language={language} />}
      {activeTab === 'users' && <AdminUsers language={language} />}
      {activeTab === 'stats' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {adminStats.map((stat) => (
              <div key={`stat-${stat.icon}`} className="bg-card rounded-2xl border border-border card-shadow p-5">
                <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-2">
                  {stat.label[language]}
                </p>
                <p className="text-2xl font-800 text-foreground font-tabular">{stat.value}</p>
                <div className={`flex items-center gap-1 mt-1 text-xs font-600 ${stat.change >= 0 ? 'text-success' : 'text-danger'}`}>
                  <span>{stat.change >= 0 ? '↑' : '↓'}</span>
                  <span>{Math.abs(stat.change)}%</span>
                  <span className="text-muted-foreground font-400">
                    {language === 'fr' ? 'vs semaine dernière' : 'vs прошлая неделя'}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <AdminStatsChart language={language} />
        </div>
      )}
      {activeTab === 'reports' && (
        <div className="space-y-4">
          <div className="bg-card rounded-2xl border border-border card-shadow p-6">
            <h2 className="text-base font-700 text-foreground mb-4">
              {language === 'fr' ? 'Rapports disponibles' : 'Доступные отчеты'}
            </h2>
            <div className="space-y-3">
              {[
                { name: language === 'fr' ? 'Rapport utilisateurs' : 'Отчет пользователей', desc: language === 'fr' ? 'Liste complète des utilisateurs avec statistiques' : 'Полный список пользователей со статистикой', format: 'CSV' },
                { name: language === 'fr' ? 'Rapport de progression' : 'Отчет о прогрессе', desc: language === 'fr' ? 'Progression des apprenants par niveau' : 'Прогресс учеников по уровням', format: 'CSV' },
                { name: language === 'fr' ? 'Rapport XP hebdomadaire' : 'Еженедельный отчет XP', desc: language === 'fr' ? 'Distribution des XP cette semaine' : 'Распределение XP за эту неделю', format: 'CSV' },
                { name: language === 'fr' ? 'Rapport des leçons' : 'Отчет по урокам', desc: language === 'fr' ? 'Taux de complétion par leçon' : 'Процент завершения по урокам', format: 'CSV' },
              ].map((report) => (
                <div key={report.name} className="flex items-center justify-between p-4 bg-secondary rounded-xl border border-border">
                  <div>
                    <p className="text-sm font-600 text-foreground">{report.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{report.desc}</p>
                  </div>
                  <button
                    onClick={() => handleExport(report.name)}
                    className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-xl text-xs font-600 hover:bg-primary/90 transition-all active:scale-95"
                  >
                    <Download size={13} />
                    {report.format}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}